import React, { useState } from 'react';
import { IoAdd, IoRemove } from 'react-icons/io5';
import './FAQ.scss';
import { Helmet } from 'react-helmet-async';
import SEO from '../../components/SEO/SEO';

interface FAQItem {
  question: string;
  answer: string;
  category?: string;
}

const faqData: FAQItem[] = [
  {
    question: 'What is GrhamDeal?',
    answer:
      'GrhamDeal is a rental property platform that connects property owners with people looking for rooms, flats, houses and other rental accommodations.',
    category: 'General',
  },
  {
    question: 'How do I list my property?',
    answer:
      'You can list your property by signing up as an owner, clicking on "Create Listing" in your dashboard, and following the step-by-step process to add your property details.',
    category: 'Owners',
  },
  {
    question: "What should I do if I'm unable to find a suitable property?",
    answer:
      'If you are unable to find a suitable property, we recommend using the "Housing Inquiry" feature for personalized assistance. You may also try adjusting your search criteria or contact our support team for further help.',
    category: 'General',
  },
  {
    question: 'Is a subscription required to use the platform?',
    answer:
      'The first 30 days are free of charge. After this trial period, a subscription is required to access premium features and exclusive listings on Grham Deal.',
    category: 'General',
  },
  {
    question: 'Is my personal information secure?',
    answer:
      'Yes, we prioritize the security of your personal information. All sensitive data is encrypted, and we adhere to industry best practices to ensure your information remains protected.',
    category: 'Security',
  },
  {
    question: 'What types of rental properties can I find on GrhamDeal?',
    answer:
      'GrhamDeal helps users discover rooms, flats, houses, PG accommodations and other rental properties listed on the platform.',
    category: 'Rentals',
  },
  {
    question: 'How can I contact a property owner?',
    answer:
      'You can use the contact options available on an eligible property listing to connect with the property owner.',
    category: 'Rentals',
  },
  {
    question: 'How can I list my rental property?',
    answer:
      'Property owners can create an account and use the listing tools in their dashboard to add their rental property details.',
    category: 'Owners',
  },
  {
    question: 'What is your refund policy?',
    answer:
      'We do not offer refunds for any subscription or purchase made on Grham Deal. We recommend reviewing the plan details carefully before completing your purchase. If you have any questions, feel free to contact our support team.',
    category: 'Billing',
  },
  // Add more FAQ items as needed
];

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const toggleAccordion = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqSchemaData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <SEO
        title="FAQ | GrhamDeal Rental Properties"
        description="Find answers to common questions about GrhamDeal, including rental property listings, finding homes, property owners, subscriptions, security and support."
        canonical="https://grhamdeal.com/faq"
        image="https://grhamdeal.com/logo-512.png"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchemaData)}
        </script>
      </Helmet>

      <div className="faq-container">
        <div className="faq-header">
          <h1>Frequently Asked Questions</h1>
          <p className="faq-subtitle">
            Find answers to common questions about Grham Deal
          </p>
        </div>

        <div className="faq-content">
          {faqData.map((faq, index) => (
            <div
              key={index}
              className={`faq-item ${activeIndex === index ? 'active' : ''}`}
            >
              <button
                className="faq-question"
                onClick={() => toggleAccordion(index)}
                aria-expanded={activeIndex === index}
              >
                <span>{faq.question}</span>
                <span className="faq-icon">
                  {activeIndex === index ? (
                    <IoRemove aria-hidden="true" />
                  ) : (
                    <IoAdd aria-hidden="true" />
                  )}
                </span>
              </button>

              <div
                className={`faq-answer ${activeIndex === index ? 'show' : ''}`}
                role="region"
                aria-hidden={activeIndex !== index}
              >
                <div className="faq-answer-content">
                  <p>{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default FAQ;
